import { useTranslation } from "@sagot/i18n";
import { Button, Combobox, Icon, Skeleton } from "@sagot/ui";
import { useMemo } from "react";
import { allTimezones, findCountry, timezoneChoices, useCountries, useCountryLabel, useCountryOptions } from "./countries";

interface CountrySelectProps {
  id?: string;
  value: string;
  onChange: (code: string) => void;
  /** Stored value; stays selectable even when the list does not know it (a legacy value). */
  saved?: string;
  disabled?: boolean;
}

/** Inline, recoverable: the list is reference data, so a failure never blocks the form. */
function CountriesLoadError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <p role="alert" className="mt-1.5 flex flex-wrap items-center gap-x-2 text-xs font-semibold text-danger">
      <Icon name="alert-triangle" className="size-3.5" />
      {t("common.countries.loadError")}
      <Button type="button" variant="ghost" size="sm" onClick={onRetry}>
        <Icon name="refresh-cw" />
        {t("common.retry")}
      </Button>
    </p>
  );
}

/**
 * Searchable country picker over `GET /v1/meta/countries`. The saved value stays selectable while
 * the list loads or when it fails (the retry sits under the field).
 */
export function CountrySelect({ id, value, onChange, saved, disabled }: CountrySelectProps) {
  const { t } = useTranslation();
  const countries = useCountries();
  const options = useCountryOptions(countries.data, value, saved);
  if (countries.isPending) {
    return (
      <div aria-busy="true" data-testid="country-skeleton">
        <Skeleton className="h-10 w-full" />
        <span className="sr-only">{t("common.loading")}</span>
      </div>
    );
  }
  return (
    <>
      <Combobox
        id={id}
        options={options}
        value={value}
        onValueChange={onChange}
        disabled={disabled}
        labels={{ placeholder: t("common.countries.placeholder"), search: t("common.countries.search"), empty: t("common.countries.empty") }}
      />
      {countries.isError ? (
        <CountriesLoadError
          onRetry={() => {
            void countries.refetch();
          }}
        />
      ) : null}
    </>
  );
}

interface TimezoneOptionsProps {
  /** Country whose zones are offered first. */
  country: string;
  /** Currently chosen (stored) zone; always selectable. */
  current: string;
  /** Label of one zone (the IANA name by default). */
  format?: (zone: string) => string;
}

const identity = (zone: string) => zone;

/** `<option>`s for a native `<select>`: the country's zones first, then every other zone. */
export function TimezoneOptions({ country, current, format = identity }: TimezoneOptionsProps) {
  const { t } = useTranslation();
  const countries = useCountries();
  const label = useCountryLabel(countries.data);
  const choices = useMemo(
    () => timezoneChoices(findCountry(countries.data, country), allTimezones(countries.data), current),
    [countries.data, country, current],
  );
  const renderZone = (zone: string) => (
    <option key={zone} value={zone}>
      {format(zone)}
    </option>
  );
  return (
    <>
      {choices.saved ? renderZone(choices.saved) : null}
      {choices.country.length > 0 ? (
        <>
          <optgroup label={t("common.timezones.inCountry", { country: label(country) })}>{choices.country.map(renderZone)}</optgroup>
          <optgroup label={t("common.timezones.all")}>{choices.others.map(renderZone)}</optgroup>
        </>
      ) : (
        choices.others.map(renderZone)
      )}
    </>
  );
}
